import { FormikErrors, FormikHelpers, useFormikContext } from 'formik';
import { debounce } from 'lodash';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { SaveStatusToast } from '../../../../../../components/toast/toast.gql';
import { trackScratchpadRecommendationCreated } from '../../../../../../config/analytics';
import { getLocalizedText } from '../../../../../../i18n/i18n.utils';
import { useIsMounted } from '../../../../../../util/hooks';
import { logger } from '../../../../../../util/logger';
import { ApplicationDefaultParameterSetLocalFormikFormValues } from '../../../../shared/component/settings/application-settings-edit';
import { useFlowServices } from '../../../../shared/hook/flow-service.hooks';
import { useScratchpadRecommendationAccess } from '../../../../shared/hook/recommendation.hooks';
import { FlowItemForm } from '../../../../shared/model/flow.types';
import { BeaconWeldingData } from '../../model/beacon-welding-data';
import { ScratchpadRecommendationFeedback } from '../settings/recommendation/recommendation-feedback';
import {
  MultipleWeldRecommendations,
  WeldSettingsRecommendation,
  setFormParamValues,
  useCreateMultipleSettingsRecommendationsForForm,
} from '../settings/recommendation/settings-recommendation.hook';
import {
  RECOMMENDATION_CONFIG_INPUTS,
  RECOMMENDATION_TRIGGER_PARAM_INPUTS,
  convertFormParametersToNumbersFromString,
  findFormDifferences,
} from '../settings/recommendation/settings-recommendation.utils';
import { WeldRecommendationsHeaderButton } from '../settings/recommendation/weld-recommendations-header-button';
import { WeldRecommendationsPopover } from '../settings/recommendation/weld-recommendations-popover';
import {
  WeldProcessSettingsFormProps,
  WeldProcessSettingsFormWithHeader,
} from './weld-process-settings-form';

interface WeldProcessSettingsWithRecommendationsProps
  extends WeldProcessSettingsFormProps {
  onRecommendSettings?: (recommendationId: string) => void;
  onRecommendSettingsLoading?: (loading: boolean) => void;
}

export const WeldProcessSettingsWithRecommendations = (
  props: WeldProcessSettingsWithRecommendationsProps
): ReactElement => {
  const {
    onRecommendSettings,
    onRecommendSettingsLoading,
    ...weldProcessSettingsProps
  } = props;

  const { flowItemService, flowService } =
    useFlowServices<BeaconWeldingData.FlowConfig>();
  const formik =
    useFormikContext<ApplicationDefaultParameterSetLocalFormikFormValues>();
  const [previousForm, setPreviousForm] =
    useState<ApplicationDefaultParameterSetLocalFormikFormValues>(
      formik.values
    );
  const [recommendations, setRecommendations] =
    useState<MultipleWeldRecommendations>();
  const [selectedRecommendationIndex, setSelectedRecommendationIndex] =
    useState(-1); // -1 means no selection
  const [isLoadingRecommendation, setLoadingRecommendation] = useState(false);
  const [recommendationFailed, setRecommendationFailed] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const createMultipleWeldRecommendationsForForm =
    useCreateMultipleSettingsRecommendationsForForm();
  const isMounted = useIsMounted();
  const { isEnabled: isScratchpadRecommendationEnabled } =
    useScratchpadRecommendationAccess();
  const equipment = flowService.getEquipmentName();

  useEffect(() => {
    if (onRecommendSettingsLoading) {
      onRecommendSettingsLoading(isLoadingRecommendation);
    }
  }, [isLoadingRecommendation, onRecommendSettingsLoading]);

  const recommendParameters = useCallback(
    async (
      currentForm: ApplicationDefaultParameterSetLocalFormikFormValues,
      previousForm: ApplicationDefaultParameterSetLocalFormikFormValues,
      setFieldValue: FormikHelpers<FlowItemForm>['setFieldValue'],
      validateForm: FormikHelpers<ApplicationDefaultParameterSetLocalFormikFormValues>['validateForm'],
      forceRecommendation = false
    ) => {
      const formErrors = await validateForm(currentForm);
      const formHasValidErrors = containsValidErrors(formErrors);

      if (formHasValidErrors) {
        return;
      }
      setPreviousForm(currentForm);

      const convertedPreviousFormParams =
        convertFormParametersToNumbersFromString(
          previousForm.data,
          flowItemService
        );

      const convertedCurrentFormParams =
        convertFormParametersToNumbersFromString(
          currentForm.data,
          flowItemService
        );

      const shouldGetRecommendation =
        forceRecommendation ||
        validateFormChangeRequiresRecommendation(
          { ...previousForm, data: convertedPreviousFormParams },
          { ...currentForm, data: convertedCurrentFormParams }
        );

      if (!shouldGetRecommendation) {
        return;
      }

      const actualForm: FlowItemForm = currentForm.data;

      // Only block if critical form structure is missing, not parameter values
      if (!actualForm.parameters) {
        return;
      }

      setLoadingRecommendation(true);
      setRecommendationFailed(false);

      try {
        const weldRecommendations =
          await createMultipleWeldRecommendationsForForm(
            actualForm,
            currentForm.tags
          );

        if (
          weldRecommendations &&
          weldRecommendations.recommendations.length > 0
        ) {
          setRecommendations(weldRecommendations);
          setSelectedRecommendationIndex(-1); // No selection until user clicks

          // Don't auto-apply recommendations - let user select manually
          // This preserves any customized settings the user may have

          if (onRecommendSettings) {
            onRecommendSettings(weldRecommendations.id);
          }
        }
      } catch (error) {
        logger.error('failed to recommend parameters for form', {
          error,
          form: actualForm,
          tags: currentForm.tags,
        });
        setRecommendationFailed(true);
      } finally {
        trackScratchpadRecommendationCreated();
        setLoadingRecommendation(false);
      }
    },
    [
      flowItemService,
      createMultipleWeldRecommendationsForForm,
      onRecommendSettings,
    ]
  );

  const recommendParametersDebounced = useMemo(() => {
    return debounce(recommendParameters, 1000);
  }, [recommendParameters]);

  useEffect(() => {
    if (!isMounted() || !isScratchpadRecommendationEnabled) {
      return;
    }

    recommendParametersDebounced(
      formik.values,
      previousForm,
      formik.setFieldValue,
      formik.validateForm
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formik.setFieldValue,
    formik.validateForm,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(formik.values),
    recommendParametersDebounced,
    previousForm,
    isMounted,
    isScratchpadRecommendationEnabled,
  ]);

  // Auto-load recommendations when component mounts
  useEffect(() => {
    if (
      isMounted() &&
      isScratchpadRecommendationEnabled &&
      !recommendations &&
      !isLoadingRecommendation
    ) {
      // Force load recommendations on mount for immediate discovery
      recommendParameters(
        formik.values,
        previousForm,
        formik.setFieldValue,
        formik.validateForm,
        true // force recommendation
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted, isScratchpadRecommendationEnabled]);

  const handleSelectRecommendation = useCallback(
    (index: number, recommendation: WeldSettingsRecommendation) => {
      setSelectedRecommendationIndex(index);

      // Apply selected recommendation to form
      setFormParamValues(
        recommendation.parameters,
        formik.setFieldValue,
        'data'
      );

      // Close popover after selection
      setIsPopoverOpen(false);
    },
    [formik.setFieldValue]
  );

  const handleOpenPopover = useCallback(() => {
    setIsPopoverOpen(true);

    // Auto-search for recommendations when opening if we don't have any
    if (
      !recommendations &&
      !isLoadingRecommendation &&
      isScratchpadRecommendationEnabled
    ) {
      recommendParameters(
        formik.values,
        previousForm,
        formik.setFieldValue,
        formik.validateForm,
        true // force recommendation
      );
    }
  }, [
    recommendations,
    isLoadingRecommendation,
    isScratchpadRecommendationEnabled,
    recommendParameters,
    formik.values,
    previousForm,
    formik.setFieldValue,
    formik.validateForm,
  ]);

  const handleClosePopover = useCallback(() => {
    setIsPopoverOpen(false);
  }, []);

  // Create the recommendations header button
  const recommendationsActionButton = isScratchpadRecommendationEnabled ? (
    <WeldRecommendationsHeaderButton
      onClick={handleOpenPopover}
      isLoading={isLoadingRecommendation}
    />
  ) : undefined;

  return (
    <>
      {/* All weld parameters with recommendations button in header */}
      <WeldProcessSettingsFormWithHeader
        {...weldProcessSettingsProps}
        header={getLocalizedText('welding:weld_settings')}
        actionButton={recommendationsActionButton}
      />

      {/* Popover with recommendations */}
      {isScratchpadRecommendationEnabled && (
        <WeldRecommendationsPopover
          isOpen={isPopoverOpen}
          onClose={handleClosePopover}
          trigger="recommendations-trigger-button"
          recommendations={recommendations}
          selectedIndex={selectedRecommendationIndex}
          equipment={equipment}
          onSelectRecommendation={handleSelectRecommendation}
          isLoading={isLoadingRecommendation}
        />
      )}

      <ScratchpadRecommendationFeedback
        recommendation={
          recommendations &&
          selectedRecommendationIndex >= 0 &&
          recommendations.recommendations[selectedRecommendationIndex]
            ? {
                ...recommendations,
                recommendation:
                  recommendations.recommendations[selectedRecommendationIndex],
              }
            : undefined
        }
        // we reset the feedback since it is a one time use
        afterSubmit={() => setRecommendations(undefined)}
      />
      <SaveStatusToast
        loading={isLoadingRecommendation}
        error={recommendationFailed}
        defaultErrorMessageKey="welding:scratchpad_settings_recommendation_failed"
        showOnly="error"
      />
    </>
  );
};

function validateFormChangeRequiresRecommendation(
  previousForm: ApplicationDefaultParameterSetLocalFormikFormValues,
  updatedForm: ApplicationDefaultParameterSetLocalFormikFormValues
): boolean {
  return findFormDifferences(
    previousForm,
    updatedForm,
    RECOMMENDATION_TRIGGER_PARAM_INPUTS,
    RECOMMENDATION_CONFIG_INPUTS
  );
}

function containsValidErrors(
  errors: FormikErrors<ApplicationDefaultParameterSetLocalFormikFormValues>
): boolean {
  const acceptableParams: string[] = [
    BeaconWeldingData.FlowItemParameterRef.TRAVEL_SPEED,
    BeaconWeldingData.FlowItemParameterRef.VOLTAGE,
    BeaconWeldingData.FlowItemParameterRef.WIRE_FEED_SPEED,
  ];
  const erroredParams = Object.keys(errors?.data?.parameters ?? {});
  const validErrors = erroredParams.filter(
    (param) => !acceptableParams.includes(param)
  );

  return validErrors.length > 0;
}
